import { useState, useEffect, useContext, createContext } from "react";
import { FunctionComponent, ReactNode, createElement } from "react";
import { UserContext } from "components/user";
import { getPreferences, savePreferences, FavoriteSchema } from "../utils/preferencesClient";
import useUser from "./useUser";

const STORAGE_KEY = "collections-preferences";
const SERVICE_NAME = "collections";

interface FavoritesContextValue {
  favorites: FavoriteSchema[];
  toggleFavorite: (metadata: FavoriteSchema) => void;
  isFavorite: (schemaKey: string) => boolean;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider: FunctionComponent<FavoritesProviderProps> = ({ children }) => {
  const { carta } = useContext(UserContext);
  const [favorites, setFavorites] = useState<FavoriteSchema[]>([]);
  const user = useUser();
  useEffect(() => {
    if (user.user !== null) return;
    localStorage.removeItem(STORAGE_KEY);
    setFavorites([]);
  }, [user]);

  useEffect(() => {
    if (!carta) return;

    let isMounted = true;

    const load = async () => {
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached && isMounted) {
          const parsed = JSON.parse(cached);
          if (parsed.forUser !== user.user?.name) {
            localStorage.removeItem(STORAGE_KEY);
          } else {
            setFavorites(parsed.favorites || []);
          }
        }
      } catch (error) {
        console.error("Failed to load favorites from localStorage:", error);
      }

      if (!isMounted) return;

      const data = await getPreferences(carta, SERVICE_NAME);
      if (data.favorites && isMounted) {
        setFavorites(data.favorites);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ forUser: user.user?.name, favorites: data.favorites }));
        } catch (error) {
          console.error("Failed to save favorites to localStorage:", error);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [carta]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveFavoritesToAPI = (newFavorites: FavoriteSchema[]) => {
    if (!carta) return;
    savePreferences(carta, SERVICE_NAME, { favorites: newFavorites });
  };

  const toggleFavorite = (metadata: FavoriteSchema) => {
    const existingIndex = favorites.findIndex(fav => fav.key === metadata.key);
    const newFavorites = existingIndex >= 0
      ? favorites.filter(fav => fav.key !== metadata.key)
      : [...favorites, metadata];

    setFavorites(newFavorites);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ favorites: newFavorites }));
    } catch (error) {
      console.error("Failed to save favorites to localStorage:", error);
    }

    saveFavoritesToAPI(newFavorites);
  };

  const isFavorite = (schemaKey: string) => favorites.some(fav => fav.key === schemaKey);

  return createElement(FavoritesContext.Provider, { value: { favorites, toggleFavorite, isFavorite } }, children);
};

export function useFavorites(): FavoritesContextValue {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used within a FavoritesProvider");
  return ctx;
}
